/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  fetchAgencyPublications,
  removePublication,
} from '../api/publications-catalog-api';
import { groupByPortal } from '../types';
import type { PortalGroup } from '../types';

export interface UseAgencyPublicationsResult {
  groups: PortalGroup[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  removeEntry: (propertyId: string, portal: string, publicationId: string) => Promise<void>;
}

/**
 * Loads an agency's portal publications, grouped by portal.
 *
 * @param agencyId - Agency scope.
 * @returns Grouped publications with loading, error, and refetch controls.
 */
export function useAgencyPublications(agencyId: string): UseAgencyPublicationsResult {
  const [groups, setGroups] = useState<PortalGroup[]>([]);
  const [loading, setLoading] = useState(Boolean(agencyId));
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    if (!agencyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAgencyPublications(agencyId);
      setGroups(groupByPortal(data.entries));
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [agencyId]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  const removeEntry = useCallback(
    async (propertyId: string, portal: string, publicationId: string) => {
      await removePublication(agencyId, propertyId, portal, { post_id: publicationId });
      await load();
    },
    [agencyId, load],
  );

  return { groups, loading, error, refresh, removeEntry };
}
